import { getSubjectLabels } from '@/lib/settings';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection } from '@/components/ui/wizard-layout';
import { cn } from '@/lib/utils';
import { useGeoapifyAddressAutocomplete } from '@/hooks/useGeoapifyAddressAutocomplete';

export type PersonAssignmentRole = 'student' | 'contract_partner';
export type PersonAssignmentMode = 'auto' | 'manual' | 'create_new';

export interface PersonAssignmentLegacyData {
  salutation: string;
  company_name?: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  email: string;
  phone: string;
  address_street: string;
  address_zip: string;
  address_city: string;
  address_country: string;
  newsletter_opt_in: boolean;
  preferred_contact_email: boolean;
  preferred_contact_phone: boolean;
}

export interface PersonAssignmentSelection {
  mode: PersonAssignmentMode;
  legacy_person_id: number;
  wp_user_id: number;
}

export interface PersonAssignmentCardProps {
  role: PersonAssignmentRole;
  legacy: PersonAssignmentLegacyData;
  onChange: (patch: Partial<PersonAssignmentLegacyData>) => void;
  assignment: PersonAssignmentSelection;
  onAssignmentChange: (patch: Partial<PersonAssignmentSelection>) => void;
  relatedPeople: ReadonlyArray<{ legacy_person_id: number; name?: string | null; role?: string | null; relation?: string | null }>;
  studentLegacyPersonId?: number;
  studentName?: string;
  faecher?: ReadonlyArray<{ id: number; name: string }>;
  ageLabel?: string;
  preferredContactEmailLocked?: boolean;
}

const formatRoleLabel = (role: PersonAssignmentRole): string => (role === 'student' ? 'Schüler' : 'Vertragspartner');

const SALUTATION_OPTIONS = [
  { value: 'Herr', label: 'Herr' },
  { value: 'Frau', label: 'Frau' },
  { value: 'Divers', label: 'Divers' },
  { value: 'Firma', label: 'Firma' },
];

const COUNTRY_OPTIONS = [
  { value: 'Deutschland', label: 'Deutschland' },
  { value: 'Österreich', label: 'Österreich' },
  { value: 'Schweiz', label: 'Schweiz' },
];

export default function PersonAssignmentCard({
  role,
  legacy,
  onChange,
  assignment,
  onAssignmentChange,
  relatedPeople,
  studentLegacyPersonId,
  studentName,
  faecher,
  ageLabel,
  preferredContactEmailLocked,
}: Readonly<PersonAssignmentCardProps>) {
  const [expanded, setExpanded] = useState(false);
  const [faecherOpen, setFaecherOpen] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressFocused, setAddressFocused] = useState(false);
  const composedAddress = `${legacy.address_street} ${legacy.address_zip} ${legacy.address_city}`.trim();
  const { suggestions: addressSuggestions } = useGeoapifyAddressAutocomplete({
    query: addressQuery,
    enabled: addressFocused,
  });

  useEffect(() => {
    if (!addressFocused) setAddressQuery('');
  }, [addressFocused]);

  const personOptions = useMemo(() => {
    const filtered = role === 'student'
      ? relatedPeople.filter((p) => p.role === 'student')
      : relatedPeople.filter((p) => p.role === 'contract_partner' || p.role === 'payer');
    const map = new Map<number, { legacy_person_id: number; name?: string | null; role?: string | null; relation?: string | null }>();
    filtered.forEach((person) => {
      map.set(Number(person.legacy_person_id), person);
    });
    if (
      role === 'contract_partner' &&
      studentLegacyPersonId &&
      studentLegacyPersonId > 0 &&
      !map.has(studentLegacyPersonId)
    ) {
      const studentEntry = relatedPeople.find((p) => Number(p.legacy_person_id) === studentLegacyPersonId);
      map.set(studentLegacyPersonId, {
        legacy_person_id: studentLegacyPersonId,
        name: studentName ?? studentEntry?.name ?? 'Schüler',
        role: 'student',
        relation: studentEntry?.relation ?? null,
      });
    }
    return Array.from(map.values());
  }, [role, relatedPeople, studentLegacyPersonId, studentName]);

  const selectedPerson = personOptions.find((p) => Number(p.legacy_person_id) === assignment.legacy_person_id);
  // Nur menschenlesbare Beziehung anzeigen (z. B. "Sohn", "Mutter") – nicht
  // den technischen Rollen-Schluessel "student"/"contract_partner".
  const relationLabel = (selectedPerson?.relation ?? '').trim();
  const fullName = `${legacy.first_name} ${legacy.last_name}`.trim() || (selectedPerson?.name ?? '');
  const titleNamePieces = [fullName, relationLabel].filter(Boolean).join(' – ');
  const titleSuffix = ageLabel ? ` (${ageLabel})` : '';
  const isContractPartner = role === 'contract_partner';
  const isCompanyCustomer = legacy.salutation === 'Firma';

  const handleAddressSelect = (s: { structured: { formatted: string; street?: string; houseNumber?: string; postalCode?: string; zip?: string; city?: string; country?: string } }) => {
    const street = [s.structured.street, s.structured.houseNumber].filter(Boolean).join(' ').trim();
    const zip = String(s.structured.zip || s.structured.postalCode || '').trim();
    const city = String(s.structured.city || '').trim();
    const country = String(s.structured.country || legacy.address_country || 'Deutschland').trim();
    onChange({
      address_street: street,
      address_zip: zip,
      address_city: city,
      address_country: country || 'Deutschland',
    });
    setAddressQuery('');
    setAddressFocused(false);
  };

  return (
    <WizardCard>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className="flex cursor-pointer items-center gap-4 rounded-md px-1 py-2 hover:bg-muted/30"
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('[data-no-toggle]')) return;
          setExpanded((prev) => !prev);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        <span className="w-36 shrink-0 text-sm font-semibold text-muted-foreground">
          {formatRoleLabel(role)}:
        </span>
        <div className="flex flex-1 items-center gap-2 text-sm">
          {isContractPartner ? (
            <div data-no-toggle className="flex items-center gap-2">
              <Select
                value={String(assignment.legacy_person_id || 0)}
                onValueChange={(v) => onAssignmentChange({ legacy_person_id: Number(v) })}
              >
                <SelectTrigger className="h-8 min-w-[260px] text-sm font-semibold">
                  <SelectValue placeholder="Vertragspartner wählen…" />
                </SelectTrigger>
                <SelectContent>
                  {personOptions.map((p) => {
                    // Nur Beziehung als Suffix anzeigen, nicht den Rollen-Schluessel.
                    const optionRel = (p.relation ?? '').trim();
                    const label = [p.name || `Legacy-Person #${p.legacy_person_id}`, optionRel].filter(Boolean).join(' – ');
                    return (
                      <SelectItem key={p.legacy_person_id} value={String(p.legacy_person_id)}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {ageLabel ? <span className="text-muted-foreground">({ageLabel})</span> : null}
            </div>
          ) : (
            <span className="font-semibold">{titleNamePieces || (selectedPerson?.name ?? 'Person wählen')}{titleSuffix}</span>
          )}
        </div>
        {faecher && faecher.length > 0 ? (
          <button
            type="button"
            data-no-toggle
            className="flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:bg-muted/30"
            onClick={(e) => {
              e.stopPropagation();
              setFaecherOpen((prev) => !prev);
            }}
          >
            <span>{getSubjectLabels().plural} ({faecher.length})</span>
            <ChevronDown className={cn('h-3 w-3 transition-transform', faecherOpen ? 'rotate-180' : '')} />
          </button>
        ) : null}
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded ? 'rotate-180' : '')} />
      </div>
      {faecher && faecher.length > 0 && faecherOpen ? (
        <div className="px-1 pb-2 pt-1 text-xs text-muted-foreground">
          {faecher.map((f) => f.name).join(', ')}
        </div>
      ) : null}

      {expanded ? (
        <>
          <WizardKVGrid>
            {isContractPartner ? (
              <WizardKVItem label="Firma">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isCompanyCustomer}
                    onCheckedChange={(checked) => {
                      if (checked === true) {
                        onChange({ salutation: 'Firma' });
                      } else if (legacy.salutation === 'Firma') {
                        onChange({ salutation: 'Herr', company_name: '' });
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">Firmenkunde aktivieren</span>
                </div>
              </WizardKVItem>
            ) : null}
            {isContractPartner && isCompanyCustomer ? (
              <WizardKVItem label="Firmenname">
                <Input
                  value={legacy.company_name ?? ''}
                  onChange={(e) => onChange({ company_name: e.target.value })}
                />
              </WizardKVItem>
            ) : null}
            <WizardKVItem label="Anrede">
              <Select
                value={legacy.salutation || ''}
                onValueChange={(v) => onChange({ salutation: v, ...(v !== 'Firma' ? { company_name: '' } : {}) })}
                disabled={isContractPartner && isCompanyCustomer}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Anrede wählen" />
                </SelectTrigger>
                <SelectContent>
                  {SALUTATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </WizardKVItem>
            <WizardKVItem label="Geburtsdatum">
              <Input type="date" value={legacy.birth_date} onChange={(e) => onChange({ birth_date: e.target.value })} />
            </WizardKVItem>
            <WizardKVItem label="Vorname">
              <Input value={legacy.first_name} onChange={(e) => onChange({ first_name: e.target.value })} />
            </WizardKVItem>
            <WizardKVItem label="E-Mail">
              <div className="space-y-2">
                <Input type="email" value={legacy.email} onChange={(e) => onChange({ email: e.target.value })} />
                <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-2 py-1.5">
                  <span className="text-xs text-muted-foreground">
                    {preferredContactEmailLocked ? 'Standardkontaktweg' : 'Bevorzugter Kontaktweg'}
                  </span>
                  <Switch
                    checked={legacy.preferred_contact_email}
                    onCheckedChange={(v) => {
                      if (preferredContactEmailLocked) return;
                      onChange({ preferred_contact_email: v });
                    }}
                    disabled={preferredContactEmailLocked}
                    aria-label="E-Mail als bevorzugten Kontaktweg verwenden"
                  />
                </div>
              </div>
            </WizardKVItem>
            <WizardKVItem label="Nachname">
              <Input value={legacy.last_name} onChange={(e) => onChange({ last_name: e.target.value })} />
            </WizardKVItem>
            <WizardKVItem label="Telefon">
              <div className="space-y-2">
                <Input value={legacy.phone} onChange={(e) => onChange({ phone: e.target.value })} />
                <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-2 py-1.5">
                  <span className="text-xs text-muted-foreground">Bevorzugter Kontaktweg</span>
                  <Switch
                    checked={legacy.preferred_contact_phone}
                    onCheckedChange={(v) => onChange({ preferred_contact_phone: v })}
                    aria-label="Telefon als bevorzugten Kontaktweg verwenden"
                  />
                </div>
              </div>
            </WizardKVItem>
          </WizardKVGrid>

          <WizardSection title="Adresse">
            <div className="space-y-3">
              <div className="relative">
                <Label htmlFor={`address-${role}`} className="text-xs text-muted-foreground">Adresse suchen</Label>
                <Input
                  id={`address-${role}`}
                  placeholder="Adresse suchen…"
                  autoComplete="street-address"
                  value={addressFocused ? addressQuery : composedAddress}
                  onFocus={() => {
                    setAddressFocused(true);
                    setAddressQuery(composedAddress);
                  }}
                  onBlur={() => {
                    window.setTimeout(() => setAddressFocused(false), 200);
                  }}
                  onChange={(e) => setAddressQuery(e.target.value)}
                />
                {addressFocused && addressSuggestions.length > 0 ? (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow">
                    {addressSuggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleAddressSelect(s)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor={`street-${role}`} className="text-xs text-muted-foreground">Straße / Hausnummer</Label>
                  <Input
                    id={`street-${role}`}
                    value={legacy.address_street}
                    onChange={(e) => onChange({ address_street: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor={`zip-${role}`} className="text-xs text-muted-foreground">PLZ</Label>
                  <Input
                    id={`zip-${role}`}
                    value={legacy.address_zip}
                    onChange={(e) => onChange({ address_zip: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor={`city-${role}`} className="text-xs text-muted-foreground">Ort</Label>
                  <Input
                    id={`city-${role}`}
                    value={legacy.address_city}
                    onChange={(e) => onChange({ address_city: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor={`country-${role}`} className="text-xs text-muted-foreground">Land</Label>
                  <Select
                    value={legacy.address_country || 'Deutschland'}
                    onValueChange={(v) => onChange({ address_country: v })}
                  >
                    <SelectTrigger id={`country-${role}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </WizardSection>

          <WizardSection title="Newsletter">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
              <span className="text-sm text-muted-foreground">Newsletter abonnieren</span>
              <Switch
                checked={legacy.newsletter_opt_in}
                onCheckedChange={(v) => onChange({ newsletter_opt_in: v })}
                aria-label="Newsletter abonnieren"
              />
            </div>
          </WizardSection>

          <WizardSection title="Zuordnung im neuen System">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">Modus</Label>
                <Select
                  value={assignment.mode}
                  onValueChange={(v) => onAssignmentChange({ mode: v as PersonAssignmentMode })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatisch zuordnen</SelectItem>
                    <SelectItem value="manual">Manuell verknüpfen</SelectItem>
                    <SelectItem value="create_new">Neues Profil anlegen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {assignment.mode === 'manual' ? (
                <div>
                  <Label className="text-xs text-muted-foreground">Sidigo-Profil-ID</Label>
                  <Input
                    type="number"
                    min={0}
                    value={assignment.wp_user_id || ''}
                    onChange={(e) => onAssignmentChange({ wp_user_id: Number(e.target.value || 0) })}
                  />
                </div>
              ) : null}
            </div>
          </WizardSection>
        </>
      ) : null}
    </WizardCard>
  );
}
